/**
 * FB-1430 — scope the mobile-navigation focus trap to the level on screen.
 *
 * FB-1430 gave the mobile drawer dialog semantics and a working focus trap:
 * focus moves in on open, Tab is contained, the page behind is inert, Escape
 * closes, focus returns to the hamburger. That is correct at the drawer
 * boundary and wrong inside it.
 *
 * The drawer is not one panel, it is a stack. Every level — the root menu, an
 * L2 submenu, an L3 submenu — is rendered up front inside `#mobile-navigation`
 * and slid into place with a transform when opened (`.menu-drawer`, revealed
 * by `.menu-opening`; see `.menu-opening.menu-drawer` in theme.css.liquid).
 * The levels *behind* the open one are still laid out, still `visibility:
 * visible`, and merely covered by the panel sitting on top of them.
 *
 * So the trap contained focus to the drawer, but within the drawer Tab walked
 * straight out of the open submenu and on through the parent level's items,
 * the level above that, the search field and the root Close button — all
 * invisible to a sighted keyboard user, because the open panel is painted over
 * them. Screen-reader browse order had the same problem.
 *
 * Closed levels were never the issue: they carry Tailwind's `invisible`
 * (`visibility: hidden`), which the trap's focusability filter already
 * excludes. The defect is entirely about the *open* ancestors of the level in
 * view.
 *
 * The fix marks everything outside the deepest open level inert, bounded by
 * the drawer root. The trap needs no change: it re-queries its focusable set
 * on every Tab and rejects anything inside an `[inert]` subtree, so narrowing
 * what is reachable narrows the trap automatically.
 *
 * Two rules, both keyed off state the theme already maintains:
 *
 *   1. A sliding level that is closed (`.menu-drawer.invisible` without
 *      `.menu-opening`) is inert. Redundant with `visibility: hidden` at rest,
 *      but not during the 500ms slide-out: the closing panel keeps its
 *      computed visibility for the length of the transition, which is a window
 *      where its links are off-screen and still tabbable.
 *   2. Everything between the deepest open level and the drawer root — the
 *      parent levels' item rows, their Back/Close headers, the search field,
 *      the account and cart rows — is inert while that level is open.
 *
 * Accordion and carousel menu blocks are untouched. They expand in place with
 * `x-collapse` and never receive `.menu-opening`, and rule 1 keys off
 * `invisible`, which only the sliding levels carry. Their collapsed panels are
 * already `hidden` by Alpine.
 *
 * Why JS rather than `:inert` bindings in Liquid: the levels are emitted from
 * six separate blocks in `sections/mobile-navigation.liquid` and three nesting
 * depths in `snippets/mobile-main-menu.liquid`, each of which would need its
 * own hand-computed depth comparison against `currentMenuLinks.length`. That
 * is ~15 bindings that must agree with each other, in a file where the same
 * markup is already duplicated per menu type. Deriving depth from the DOM
 * instead means one rule, and it keeps working if a menu type is added.
 */
(() => {
  'use strict';

  const ROOT_ID = 'mobile-navigation';

  /** A level in the sliding stack — as opposed to an accordion panel. */
  const LEVEL_SELECTOR = '.menu-drawer.invisible';
  const OPEN_CLASS = 'menu-opening';

  /** Attributes we added on the last sync, so they can be reverted exactly. */
  let marked = null;

  /** The deepest open level at the last sync, to detect in/out transitions. */
  let lastOpen = null;

  const api = () => window.FarmacyA11y || {};

  /**
   * The open level nearest the top of the stack.
   *
   * Depth is counted in DOM ancestors rather than read from
   * `$store.xMobileNav.currentMenuLinks`, because that array is a flat list of
   * opaque path tokens with no ordering guarantee — `removeActiveLink` splices
   * by value — and the accordion branch pushes to it without ever removing.
   * The DOM cannot disagree with itself.
   */
  const deepestOpenLevel = (root) => {
    let deepest = null;
    let deepestDepth = -1;

    for (const level of root.querySelectorAll(`${LEVEL_SELECTOR}.${OPEN_CLASS}`)) {
      let depth = 0;
      for (let node = level.parentElement; node && node !== root; node = node.parentElement) depth++;
      if (depth > deepestDepth) {
        deepestDepth = depth;
        deepest = level;
      }
    }

    return deepest;
  };

  /**
   * The control that opened `level`.
   *
   * Every level is emitted immediately after the row holding its disclosure
   * button, at all three depths and in both the section and the snippet, so
   * the previous element sibling is that row. Used to put focus back where the
   * user left it when they go Back.
   */
  const openerFor = (level) => {
    const row = level.previousElementSibling;
    if (!row) return null;
    return row.matches('button') ? row : row.querySelector('button');
  };

  /**
   * Move focus into a newly opened level.
   *
   * Deferred through `focusWhenReady` rather than focused directly: the level
   * is revealed by a class change that starts a 500ms transform-and-visibility
   * transition, and on the frame the class lands its children can still fail a
   * visibility test. `focusWhenReady` polls by frame until a focusable child
   * exists, and gives up if the level closes again first. It moves focus
   * without engaging a second trap, which is what we want — the drawer's own
   * trap is already active and now covers exactly this level.
   */
  const focusInto = (level) => {
    const store = window.Alpine && window.Alpine.store && window.Alpine.store('xFocusElement');
    if (store && typeof store.focusWhenReady === 'function') {
      store.focusWhenReady(level);
      return;
    }
    requestAnimationFrame(() => {
      const first = level.querySelector('button, a[href], input, select, textarea');
      if (first) first.focus();
    });
  };

  const focusOpener = (level) => {
    const opener = openerFor(level);
    if (!opener) return;
    requestAnimationFrame(() => {
      const focusable = api().isFocusable;
      if (typeof focusable === 'function' && !focusable(opener)) return;
      opener.focus();
    });
  };

  const sync = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;

    const { markInertOutside, unmarkInert } = api();
    if (typeof markInertOutside !== 'function') return;

    if (marked) {
      unmarkInert(marked);
      marked = null;
    }

    // Closed levels (rule 1). Applied whether or not the drawer is open, so a
    // level sliding out is sealed for the whole of its transition.
    const closed = [];
    for (const level of root.querySelectorAll(LEVEL_SELECTOR)) {
      if (level.classList.contains(OPEN_CLASS)) continue;
      if (level.hasAttribute('inert')) continue;
      level.setAttribute('inert', '');
      closed.push({ el: level, addedInert: true, addedAriaHidden: false });
    }

    const drawerOpen = root.classList.contains(OPEN_CLASS);
    const open = deepestOpenLevel(root);

    // The drawer itself being shut is handled by the `:inert` binding on the
    // root in sections/mobile-navigation.liquid; nothing to scope inside it.
    const behind = open && drawerOpen ? markInertOutside(open, root) : [];

    marked = closed.concat(behind);

    if (open === lastOpen) return;

    const previous = lastOpen;
    lastOpen = open;

    /**
     * Focus only follows the level change while the drawer is on screen.
     * `currentMenuLinks` is not reset when the drawer closes, so the level
     * state can change with the drawer shut — on a theme-editor section
     * reload, for instance — and moving focus then would yank it out of the
     * page for no reason.
     */
    if (!drawerOpen) return;

    if (open && (!previous || !open.contains(previous))) {
      // Descended into a level the user just opened.
      focusInto(open);
    } else if (previous) {
      // Went Back: `open` is now an ancestor of the level we left, or the
      // stack is empty and we are on the root menu.
      focusOpener(previous);
    }
  };

  /**
   * Coalesce a burst of mutations into one sync per frame. Alpine touches
   * `class` on carets, active states and carousels all over this subtree, so
   * the observer fires far more often than the level state actually changes.
   */
  let scheduled = false;
  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      sync();
    });
  };

  const observeRoot = (root) => {
    sync();

    // `class` only. The sweep writes `inert` and `aria-hidden`, so filtering to
    // `class` is what keeps this observer from re-triggering on its own work.
    new MutationObserver(scheduleSync).observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  };

  /**
   * The drawer does not exist at load. `layout/theme.liquid:141` wraps it in
   * `<template x-if="!runDispatch">`, and `runDispatch` only flips on the first
   * mouseover or touchstart — the theme's deferred-hydration trick. So this
   * waits for the node rather than assuming it, and stops watching once it has
   * it.
   */
  const start = () => {
    const existing = document.getElementById(ROOT_ID);
    if (existing) {
      observeRoot(existing);
      return;
    }

    const bootstrap = new MutationObserver(() => {
      const root = document.getElementById(ROOT_ID);
      if (!root) return;
      bootstrap.disconnect();
      observeRoot(root);
    });

    bootstrap.observe(document.body, { subtree: true, childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
